import React, { useEffect, useContext, useState, useRef } from 'react'
import { Box, Slider } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import LanguageContext from "../../../contexts/LanguageContext"
import UserContext from "../../../contexts/UserContext"


import { TopBar, CenterContainer, BlocIllustre, useApercuPleineHauteur , BandeReponse, LARGEUR_QUESTION, LARGEUR_CARTES, useRaccourciClavier, saisieEnCours, ROGNE_HAUT, RESERVE_ACCENTS, retraitPlanche, HAUTEUR_PLANCHE, MARGE_QUESTION } from "./FormatsHelpers"
import { APERCU_EDITO } from "./apercu"

import EditableField from "./EditableField"
import EditableButton from "./EditableButton"

import EditorPopover from "../../ui/Admin/Formations/EditorPopover"
import QuestionnaireV2Helper from "../../ui/Admin/Formations/FormatHelpers/QuestionnaireV2Helper"

// Largeur de la piste du curseur, relevee sur l'artboard Figma remis a 1512px.
/* ⚠️ 750 ET NON 630, ET C'EST UNE CORRECTION DE MA PROPRE MESURE.
   Le rapport du lot G opposait « piste 630 px » a « 892 px sur Figma ». Les deux
   chiffres ne designaient pas le meme objet : dans le node 3:261993, `w-[892px]`
   est la RANGEE DE LIBELLES, et la piste elle-meme est `w-[750px]` — la meme
   largeur que la colonne de reponses de tous les autres ecrans de question.
   La comparaison etait fausse ; l'ecart reel est 630 -> 750. */
const LARGEUR_PISTE = 750

/* ── ORDRE DE L'ÉCHELLE : POURQUOI DEUX CONVERSIONS ────────────────────────
 *
 * Ce qui est ENREGISTRÉ est `userChoice`, c'est-à-dire l'INDEX dans
 * `format.responses` — jamais une position à l'écran. Le score s'en déduit par
 * `responses[userChoice].points`. La position, elle, n'est qu'un affichage.
 *
 * Or MUI place le minimum d'un curseur À GAUCHE en horizontal, mais EN BAS en
 * vertical. En passant le Mobile en vertical (18/09) pour suivre le Figma,
 * j'ai donc, sans le voir, mis la PREMIÈRE réponse en bas : l'échelle se lit à
 * l'envers du node `3:282127`, qui range les libellés du premier au dernier de
 * haut en bas.
 *
 * ⛔ La correction ne touche PAS la sémantique. On inverse seulement l'échelle
 * d'AFFICHAGE en vertical, et on convertit aux deux bornes — à la lecture de
 * la valeur, et à l'écriture du choix. Un même libellé enregistre donc
 * exactement le même index qu'avant, dans les deux orientations. Les réponses
 * déjà en base gardent leur sens : elles ne sont pas relues autrement, elles
 * s'affichent simplement du bon côté.
 *
 * `n` est le nombre de réponses.
 *   horizontal : index i  →  valeur i + 1   (1 à gauche)
 *   vertical   : index i  →  valeur n - i   (n en haut, car MUI met le max en haut)
 */
export const valeurDuCurseur = (index, n, vertical) => (vertical ? n - index : index + 1)
export const indexDepuisCurseur = (valeur, n, vertical) => (vertical ? n - valeur : valeur - 1)


/* Gris du rail, des crans et de la poignee avant choix, palier par palier :
   #D4D4D4 en Mobile (3:282148), #E8E8E8 en Tablet et Desktop (3:261996). */
const GRIS_CURSEUR = ["#D4D4D4", "#E8E8E8", "#E8E8E8", "#E8E8E8"]

/* Retrait de la zone haute : la planche a sa hauteur, l'ancien en dessous
   (meme regle que le quiz, PD-23, `retraitPlanche`). */
const RETRAIT_QUESTIONNAIRE = [
  retraitPlanche(24, 80, HAUTEUR_PLANCHE.mobile),       /* 3:282129 : 80 px */
  retraitPlanche(32, 162, HAUTEUR_PLANCHE.tablette),    /* 3:282070 : zone a y=77 + 160, pastille a 237 */
  retraitPlanche(40, 120, HAUTEUR_PLANCHE.desktop),     /* md : pas de planche, suit le Desktop */
  retraitPlanche(48, 120, HAUTEUR_PLANCHE.desktop),     /* 3:261985 : 120 px */
]
/* Mobile : ecart entre deux crans du curseur vertical (3:282150 : 6 crans sur
   220 px, crans de 13 px). */
const PAS_VERTICAL = 41.4
const FormatQuestionnaireV2 = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor, sequenceSubMenu, setSequenceSubMenu }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)

  const { manageFormatResponse, manageSlide } = useContext(UserContext)

  /* ⛔ LE QUESTIONNAIRE EST UN ECRAN DE QUESTION, COMME LE QUIZ (09/09/2026).
     Il n'a jamais porte d'image : son contenu etait donc centre, quand celui
     du quiz voisin se ferre a gauche. 255 ecrans des sept modules de Nawal
     sont dans ce cas — le plus gros bloc restant apres Texte, Quizz et Saisie.
     L'ancien lecteur ne faisait pas cette difference : `FormatQuestionnaireV2`
     y rendait deja son contenu dans `MaskQuizz`, le meme panneau que le quiz.
     On lui rend donc le meme traitement, par les memes valeurs.
     ⚠️ Les ecrans de TITRE, de DIAGNOSTIC et de FEEDBACK restent centres : ce
     sont des respirations et des cartes de resultat, pas des questions. */
  const pleineHauteur = useApercuPleineHauteur()
  /* Le cote de l'image alterne d'un ecran a l'autre, regulierement. */
  /* Ce format ne porte jamais d'image : ancrage a gauche, sans alternance. */
  const cote = pleineHauteur ? "droite" : null
  const [isAlreadyAnswer, setIsAlreadyAnswer] = useState(false)
  const [userChoice, setUserChoise] = useState(null)
  useEffect(() => {
    if (format?.isDone) {
      setIsAlreadyAnswer(true)
      if (format?.userChoice?.length > 0) {
        setUserChoise( parseInt(format?.userChoice[0]) )
      } else {
        setUserChoise(null)
      }
    } else {
      setIsAlreadyAnswer(false)
      setUserChoise(null)
    }
  }, [format])

  /* ⚠️ L'orientation se calcule AVANT les marks : ce sont elles qui en
     dependent desormais. */
  const theme = useTheme()
  const curseurVertical = useMediaQuery(theme.breakpoints.down("sm"))
  const nbReponses = format?.responses?.length || 1

  const marks = []
  for(var i=0; i < (format?.responses?.length || 0); i++){
    marks.push({value: valeurDuCurseur(i, nbReponses, curseurVertical), label: format?.responses[i]?.label})
  }

  // Cote Figma : piste de 750px (node 3:261993), libelles centres sous leur cran
  // et repartis a parts egales.
  // Point 5 : « rien n'a encore ete choisi » est un etat a part entiere, il ne
  // se deduit pas de la valeur affichee — laquelle vaut 1 dans les deux cas.
  /* ⛔ MOBILE : LE MEME CURSEUR, EN VERTICAL — ET CE N'EST PAS UNE AUTRE
     MANIERE DE REPONDRE. J'avais d'abord lu la planche Mobile comme une liste de
     choix, donc comme une decision produit. Le node 3:282127 dit autre chose :
     un rail VERTICAL de 220 px (3:282147), six pastilles de 13 px alignees
     dessus, et les six libelles dans une colonne a gauche. Meme controle, memes
     valeurs, meme geste — seule l'orientation change. Il n'y avait donc rien a
     arbitrer, seulement a chercher dans le node. */
  const rienChoisi = userChoice === null || userChoice === undefined
  /* Largeur d'un libelle extreme ancre par son bord (voir les libelles). */
  const partAncree = nbReponses <= 2 ? 50 : (100 * (nbReponses + 1)) / (2 * nbReponses * (nbReponses - 1))
  const partCentree = 100 / nbReponses
  const largeurAncree = nbReponses >= 3
    ? [`${partAncree}%`, `${partAncree}%`, `${partCentree}%`, `${partCentree}%`]
    : `${partAncree}%`

  /* Le clic et la touche passent par la MEME fonction. */
  const valider = () => {
    /* Deja repondu : on avance, sans reecrire — comme la saisie (B72) et le
       quiz. Le clic ne faisait RIEN, et apres une reprise en debut de partie
       l'apprenant restait bloque (02/10/2026, en rejouant B73). */
    if (isAlreadyAnswer) { manageSlide(1); return }
    if (rienChoisi) return
    manageFormatResponse(format, [{ key: 'isDone', value: true }, { key: 'userChoice', value: [userChoice] }])
    manageSlide(1)
  }
  useRaccourciClavier(isEditor !== true && onlyContent !== true, (e) => {
    if (e.key !== "Enter" || saisieEnCours(e)) return
    e.preventDefault()
    valider()
  })


  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ LE FORMAT DEVIENT SON PROPRE REFERENTIEL DE MESURE (08/09/2026).
         Nawal voit « de grosses differences » entre l'apercu de l'admin et la
         vue apprenant. Cause : l'apercu ne rend pas l'ecran a la taille de la
         fenetre. `ScaleThumbForm` le rend dans une boite FIXE de 1500 x 1000,
         puis la reduit par un `transform: scale()`. Or `vw` et `vh` se
         calculent sur la FENETRE, jamais sur cette boite : les proportions
         divergeaient donc des deux cotes, sans qu'aucun reglage puisse les
         reconcilier — ce sont deux referentiels differents.
         En declarant ce conteneur, les unites `cqw`/`cqh` employees plus bas se
         mesurent sur LUI. Dans le lecteur il occupe la fenetre : le rendu ne
         bouge pas d'un pixel. Dans l'apercu il fait 1500 x 1000 : le rendu
         devient fidele. */
      containerType:"size",
      overflow:"hidden",
      /* ⛔ PAS DE GAP RACINE (22/09). La planche colle le contenu au bandeau :
         le cadre de contenu commence exactement a y=75, et la bande basse est
         adjacente elle aussi. Ces 10 px repoussaient tout l'ecran vers le bas et
         comptaient pour 10 des 22 px de decalage mesures sur la pastille. */

      /* ⛔ L'ancre du panneau : sans elle le bloc absolu se cale sur la fenetre
         et passe sous les deux bandeaux (erreur du 08/09). */
      position: APERCU_EDITO ? "relative" : "static",
    }}>
      { /* EDITOR ADMIN */}
      {isEditor &&
        <EditorPopover
          open={sequenceSubMenu}
        >
          <QuestionnaireV2Helper
            format={format}
          />
        </EditorPopover>
      }
      { /* END EDITOR ADMIN */}

      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      <CenterContainer
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={onlyContent}
        /* ⛔ L9 EST REMPLACEE PAR LE FIGMA DU 17/09, ET C'EST TRACE.
           L9 (Valentin, 13/05/2026) disait « Retirer l'element au-dessus des
           questions ». C'est une preference purement VISUELLE, sans regle de
           comportement, du meme auteur que les planches, et anterieure de
           quatre mois. Les planches actuelles montrent la pastille de
           sous-chapitre sur CHAQUE ecran de question — quiz 3:282231,
           questionnaire 3:241965, saisie 3:40519, quiz illustre 4:559.
           Regle appliquee : a preference visuelle egale, la source la plus
           recente du meme auteur l'emporte. */
        noPartInfos={false}
        cote={cote}
        /* Lot H, meme regle que le quiz : Figma ne met pas de marge laterale sur
           la zone de contenu, il en met sur la zone haute et sur la bande. */
        sansMargesLaterales
        /* 03/10/2026 — meme regle que le quiz (PD-23) : la zone haute de la
           planche part a 120 px de l'en-tete (`pt-[120px]`, 3:261985), 160 en
           Tablet (3:282070), 80 en Mobile (3:282129), a la hauteur de la
           planche ; sur un ecran plus bas, le retrait retombe aux anciennes
           valeurs. */
        retraitHaut={RETRAIT_QUESTIONNAIRE}
      >
        <BlocIllustre
          image={null}
          horsFlux={cote}
          sx={{ flex:1, minHeight:0 }}
        >
        <Box sx={{
          display:"flex",
          flexDirection:"column",
          /* ⛔ LOT H — `space-between`, ET C'EST LA REGLE FIGMA, PAS UN GOUT.
             Le node 3:241965 pose `justify-between` sur la zone de contenu :
             l'enonce en haut, la bande en bas, le vide au milieu. `center`
             collait les deux au centre et laissait la bande flotter. */
          justifyContent:"space-between",
          flex:1,
          width:"100%",
          minHeight:0,
          /* Ce format n'a jamais d'image : la largeur d'apercu qui le bornait a
             720 px bornait aussi sa bande, que la planche veut pleine largeur.
             On ne garde que l'ancrage. */
          ...(pleineHauteur ? { position:"relative", zIndex:1 } : {}),
        }}>
        <EditableField
          field={format?.questions}
          type="questions"
          isEditor={isEditor}
          sx={{
            /* Meme arbitrage que sur le quiz : l'enonce cede avant que le bas de
               l'ecran ne sorte du cadre. Sans cela, mesure a 1512x600, la rangee
               de controles tombait 27 px sous le bord de la fenetre et il fallait
               faire defiler la PAGE pour atteindre le bouton — ce que la fiche de
               Nawal interdit explicitement. */
            flexShrink:1,
            minHeight:0,
            overflowY:"auto",
            ...RESERVE_ACCENTS,  /* place au-dessus de l'enonce rogne : voir FormatsHelpers */
            /* Figma, zone haute du node 3:241965 : bloc d'enonce a x=256, large
               de 1000 de TEXTE (3:261991), marges en plus — comme le quiz depuis
               le 03/10. Borne a 1000 marges comprises, il en faisait 920 et
               passait de deux a trois lignes (comparateur, section 07). */
            width:"100%",
            maxWidth:`calc(${LARGEUR_QUESTION} + ${2 * MARGE_QUESTION}px)`,
            alignSelf:"center",
            paddingLeft:["25px", `${MARGE_QUESTION}px`, `${MARGE_QUESTION}px`, `${MARGE_QUESTION}px`],
            paddingRight:["25px", `${MARGE_QUESTION}px`, `${MARGE_QUESTION}px`, `${MARGE_QUESTION}px`],
            boxSizing:"border-box",
            color:"text.black",
            /* ⚠️ Le noeud du questionnaire (3:261990) a une geometrie
               STRICTEMENT identique a celle du quiz : cadre 1000x159, titre
               1000x76, consigne 650x43 a y=116. Il porte donc la meme echelle,
               « Text/Desktop/Heading/H1 » : 42 px / 1,15, et 26 px / 1,25 en
               Mobile. L'echelle 24/30/32/34 n'avait aucune contrepartie. */
            fontSize:["26px", "42px", "42px", "42px"],
            lineHeight:["1.25", "1.15", "1.15", "1.15"],
            ...ROGNE_HAUT,  /* haut des capitales, comme la maquette : voir FormatsHelpers */
            textAlign: pleineHauteur ? ["center", "center", "center", "left"] : "center",
          }}
        />
        {/* ⛔ POINT 5 DE LA REVUE APPRENANT (Nawal, 04/09) — LES TROIS ETATS.
            `value={userChoice + 1}` avec `userChoice` a null vaut 1 : la
            poignee NOIRE etait donc posee sur le premier palier avant toute
            reponse. Deux consequences, exactement celles relevees :
              — l'apprenant croit avoir repondu, mais le bouton reste grise et
                rien ne lui dit pourquoi ;
              — choisir le premier palier est presque impossible : la poignee y
                est deja, donc la valeur ne CHANGE pas et `onChange` ne se
                declenche jamais. `onChangeCommitted`, lui, part au relachement
                meme sans changement de valeur : c'est lui qui enregistre.
            Trois etats desormais : rien de choisi (poignee grise, rail vide),
            choisi (rail noir jusqu'a la poignee), valide (rail vert, poignee
            cochee). */}
        <BandeReponse
          /* ⛔ 01/10/2026 — « ↵ pour valider » (point 3 d'Hicham). Les quatre
             planches de la section 07 le portent, node 3:262011 en Desktop. La touche ne vaut qu'une fois
             le curseur pose : avant, le bouton est desactive et la touche ne
             doit rien faire — sinon elle donnerait l'impression d'un ecran
             bloque. Le repere, lui, reste affiche : il annonce le geste. */
          reperes={[{ touche:"entree", libelle: languageValues?.formation?.toucheValider?.[actLanguage] }]}
          /* Node 3:261993 : bande de 400 px, pleine largeur, `pt-[60px]
             pb-[30px] px-[60px]`, curseur en haut et rangee de controles en bas.
             Le curseur appartient donc a la bande, pas a la zone haute. */
          /* ⛔ 06/10/2026 — REVIEW DE NAWAL DU 5 OCT, POINT 1.3 (tranche par
             elle) : 444 PX ET NON 400, la meme bande que le quiz QCM, en Desktop
             et en Tablet. ECART VOULU a la planche 3:261993 (400 px), A NOTER
             EN PD (Hicham). Plancher aligne sur celui du quiz (400) : les deux
             ecrans cedent de la meme facon sur une fenetre basse. Mobile
             inchange. */
          hauteur="444px"
          hauteurMin="400px"
          /* Node 3:261993 : la rangee de libelles fait `w-[892px]`, la piste
             `w-[750px]`, et la rangee de controles `w-[750px]`. Les libelles
             debordent donc la piste par construction — de 94 px de chaque cote,
             mesure — et il leur faut cette largeur-la pour tenir. */
          /* ⚠️ PAS 892 px : LA PLACE QU'IL FAUT, ET ELLE DEPEND DU CONTENU.
              Les libelles sont poses en absolu, centres sur leur cran, larges de
              100/n % de la piste : celui du premier cran deborde donc de la
              moitie de sa largeur a GAUCHE de la piste, et celui du dernier
              autant a droite. Avec quatre paliers, cela fait 94 px de chaque
              cote — plus que les 892 px de la planche, qui en montre six.
              Borner a 892 rognait « pas du tout » en « u tout ». On laisse donc
              la bande donner sa largeur, et ce sont ses marges de 60 px qui
              bornent. La rangee de controles, elle, garde les 750 px du node. */
           largeur="100%"
           largeurControles={LARGEUR_CARTES}
          /* Tablet : bande de 400 px collee en bas, comme la planche (3:282078). */
          hauteurFixeTablette
          /* ⛔ 04/10/2026 — MOBILE : 20 px DE MARGE DONNES AU CURSEUR, PRIS SUR LA
             BANDE. MUI centre chaque libelle sur son cran : celui du haut
             deborde du curseur (10 px sur une ligne, 20 sur deux), et le bloc
             defilant de la bande le rognait (relecture du 04/10). Le conteneur
             du curseur porte donc 20 px en haut et en bas ; la bande en rend
             13,5 en haut (6,5 au lieu de 20) et l'ecart en rend 13,5 en bas :
             premier cran a 20 px du haut de la bande et 20 px entre le dernier
             cran et le bouton, comme la planche (3:282137), bande de 340 px. */
          hautMobile={6.5}
          ecart={["6.5px", "20px", "20px", "20px"]}
          onlyContent={isEditor ? false : onlyContent}
          controles={
          <EditableButton
            isEditor={false}
            disabled={!isAlreadyAnswer && userChoice == null}
            type="button"
            /* ⛔ POINT 1.3 DE LA FICHE V2. Le libelle etait celui saisi
               dans le contenu, d'ou les « C'est parti ! » et « On
               continue ? » qui trainaient depuis des annees. Il est
               desormais CALCULE, et la regle ecrase le contenu :
               le clic ENREGISTRE la position du curseur.
               Le champ de saisie libre est retire avec lui
               (isEditor={false}) : tant qu'il existait, le libelle
               rederivait au module suivant. */
            field={languageValues?.validate?.[actLanguage]}
            sx={{}}
            onClick={valider}
          />
          }
        >
        {/* ⛔ LA GOUTTIERE DE LA POIGNEE, POSEE SUR UN CONTENEUR ET NON SUR LE
            CURSEUR. `marginLeft/Right:auto` ne centre que s'il reste de la
            place : sous ~855 px de large, la piste remplit son conteneur, la
            poignee du PREMIER cran se retrouve centree sur le bord gauche, et sa
            moitie est rognee par l'`overflowX:hidden` de la bande. Mesure : 11 px
            coupes entre 640 et 830 px de large, 9 px en dessous — et dans l'ETAT
            INITIAL, avant toute reponse, quand la poignee est justement sur le
            premier palier.
            ⚠️ PAS sur le curseur lui-meme : son rail est positionne en absolu,
            donc cale sur la boite de PADDING. Un padding l'elargit au lieu de
            l'ecarter du bord — essaye et mesure : le rail passait de 750 a
            772 px et depassait le bouton de 20 px. Sur un conteneur, la
            gouttiere ecarte sans rien elargir, et le curseur garde ses 750 px
            centres. */}
        <Box sx={{
          width:"100%",
          paddingLeft:"11px",
          paddingRight:"11px",
          /* Mobile : la place des libelles qui debordent du curseur (voir la
             bande ci-dessus). */
          paddingTop: curseurVertical ? "20px" : 0,
          paddingBottom: curseurVertical ? "20px" : 0,
          boxSizing:"border-box",
          display:"flex",
          flexDirection:"column",
        }}>
        <Slider
          orientation={curseurVertical ? "vertical" : "horizontal"}
          /* ⛔ 04/10/2026 — EN VERTICAL, LE RAIL SE REMPLIT DEPUIS LE HAUT (B82).
             La premiere reponse y est en haut, a la valeur maximale : MUI
             remplissait depuis le bas, donc de la poignee vers la DERNIERE
             reponse, a l'envers de l'horizontal. `inverted` designe comme
             « actifs » les crans du haut (effaces sous le remplissage) ; MUI
             garde la geometrie de la piste (du bas a la poignee) et n'echange
             que ses propres couleurs : le `sx` echange donc rail et piste. */
          track={curseurVertical ? "inverted" : "normal"}
          disabled={isAlreadyAnswer}
          /* Les deux bornes de la conversion. Tant que rien n'est choisi, la
             poignee se pose sur la PREMIERE reponse — a gauche en horizontal,
             en haut en vertical : le meme palier dans les deux cas. */
          value={rienChoisi
            ? valeurDuCurseur(0, nbReponses, curseurVertical)
            : valeurDuCurseur(userChoice, nbReponses, curseurVertical)}
          onChange={(event, newValue) => {
            setUserChoise(indexDepuisCurseur(newValue, nbReponses, curseurVertical))
          }}
          onChangeCommitted={(event, newValue) => {
            if (isAlreadyAnswer) return
            const v = Array.isArray(newValue) ? newValue[0] : newValue
            if (typeof v === "number") setUserChoise(indexDepuisCurseur(v, nbReponses, curseurVertical))
          }}
          sx={{
            borderRadius:"0px !important",
            /* Mesure au comparateur (03/10/2026) : le centre du rail tombe a
               60 + 9 px du haut de la bande Desktop (3:261996, y=651 pour une
               bande a 582 et 30 px de marge), 50 + 9 en Tablet (3:282081). La
               racine du curseur fait 29 px, rail centre. */
            marginTop:["20px", "14.5px", "24.5px", "24.5px"],
            marginBottom:["40px", "70px",],
            /* ⛔ 04/10/2026 — PADDING FIXE, Y COMPRIS SUR ECRAN TACTILE. MUI passe
               la racine a `padding:20px 0` sous `pointer: coarse` (43 px au lieu
               de 29) : sur une tablette tactile, le rail descendait de 7 px et la
               poignee mordait sur les libelles, cales a la souris (relecture du
               04/10). Une seule cle, qui choisit l'axe. */
            padding: curseurVertical ? "0 5px" : "13px 0",
            // 750px sur le node 3:261993 : la meme colonne que les cartes du
            // quiz et que la rangee de controles de tous les ecrans de question.
            /* ⛔ CENTREE, et ce n'est pas un detail : dans un parent en colonne
               sans alignement, une largeur maximale ferre a GAUCHE. La piste
               s'arretait alors 73 px avant le bord droit du bouton, alors que le
               node les aligne (piste 385-1135, controles 381-1131). */
            maxWidth: curseurVertical ? "13px" : LARGEUR_PISTE + "px",
            /* En vertical, la planche pose le rail a droite d'une colonne de
               libelles de 300 px : on le ferre a droite plutot que de le
               centrer. */
            marginLeft: curseurVertical ? "auto" : "auto",
            marginRight: curseurVertical ? "0px" : "auto",
            /* ⛔ UNE SEULE CLE `height`, ET ELLE EST CONDITIONNELLE.
               Deux tentatives ont echoue avant celle-ci, pour la meme raison :
               je posais la hauteur verticale dans un spread, et la ligne
               `height:["4px","8px"]` — l'epaisseur du rail HORIZONTAL — la
               reecrasait quelques lignes plus bas, puisqu'elle vient apres. Le
               rail retombait a 8 px et les six libelles se superposaient.
               Empiler un override de plus n'aurait pas corrige la cause : deux
               cles de meme nom dans un meme objet, c'est la derniere qui gagne.
               Une seule cle, qui choisit. */
            /* Rail de 3 px (trait de 3 de Line 442, 3:261996 et 3:282148). */
            /* Vertical : la colonne de la planche fait 220 px CRANS COMPRIS
               (3:282150, du haut du premier cran au bas du dernier) ; un cran
               deborde de 6,5 px a chaque bout du rail, d'ou 207 + 2 x 6,5. */
            /* ⚠️ ET LE PAS, PAS LA HAUTEUR, VIENT DE LA PLANCHE : 41,4 px entre deux
               crans (3:282150). A hauteur fixe, sept reponses ou plus resserraient les
               libelles jusqu'a les faire se chevaucher (relecture du 04/10). */
            height: curseurVertical ? `${Math.max(nbReponses - 1, 1) * PAS_VERTICAL}px` : "3px",
            /* Vertical : les marges sont sur le conteneur (place des libelles). */
            ...(curseurVertical ? { width:"3px", marginTop:0, marginBottom:0 } : {}),
            /* ⛔ 03/10/2026 — RIEN DE CHOISI : LA POIGNEE DE LA PLANCHE. Carre de
               15 px, gris #E8E8E8, trait blanc de 2 px, sans ombre (3:261997,
               3:282082). Le code posait 22 px gris fonce, cadre de 4 px et ombre :
               il annoncait un choix qui n'existait pas. Une fois choisie, la
               poignee de la planche « choisi » (34:1850, 22 px) reprend. */
            "& .MuiSlider-thumb" : rienChoisi ? {
              borderRadius:"0px",
              width:"15px",
              height:"15px",
              /* Trait blanc EXTERIEUR, comme la planche (3:261997, contour hors
                 de la forme) : la poignee garde ses 15 px de gris. */
              border:"none",
              outline:"2px solid white",
              boxShadow:"none",
              backgroundColor: GRIS_CURSEUR,
              "&::before" : { boxShadow:"none" },
              /* Mobile : la planche ne dessine pas de poignee avant choix
                 (3:282150) ; on touche un cran pour choisir. Elle reste dans
                 le DOM, focalisable (voir l'anneau de focus plus bas). */
              ...(curseurVertical ? { opacity:0 } : {}),
            } : {
              borderRadius:"0px",
              width:["18px", "22px"],
              height:["18px", "22px"],
              border:["2px solid white","4px solid white"],
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
              /* Etat 1 : gris tant que rien n'est choisi. Etat 3 : vert une
                 fois valide. Entre les deux, le noir par defaut. */
              backgroundColor: isAlreadyAnswer ? "#53D769" : undefined,
            },
            /* Etat 3 : la poignee porte une coche. Deux traits en bordure,
               tournes : pas d'image a charger, et l'echelle suit la poignee. */
            "& .MuiSlider-thumb::after" : isAlreadyAnswer ? {
              content: '""',
              position:"absolute",
              width:["4px", "5px"],
              height:["8px", "10px"],
              borderRight:"2px solid white",
              borderBottom:"2px solid white",
              transform:"rotate(45deg) translate(-1px, -1px)",
              borderRadius:0,
              top:"auto", left:"auto", right:"auto", bottom:"auto",
            } : {
              content:"none",
            },
            /* Etat 2 : le rail se remplit jusqu'a la poignee des qu'une valeur
               est choisie — et RESTE vide tant que rien ne l'est. */
            "& .MuiSlider-track" : (curseurVertical && !rienChoisi) ? {
              /* Vertical, une fois choisi : la piste (du bas a la poignee) est
                 la partie NON remplie, grise, opaque (MUI la rend translucide en
                 `inverted`), jusqu'au bout du trait, 6,5 px sous le rail. */
              border:"none",
              backgroundColor: GRIS_CURSEUR,
              opacity:1,
              bottom:"-6.5px !important",
            } : {
              border:"none",
              backgroundColor: rienChoisi ? "transparent" : (isAlreadyAnswer ? "#53D769" : "#0A0A0B"),
            },
            "& .MuiSlider-thumb.Mui-active" : {
              boxShadow: "0px 0px 0px 8px rgba(0, 0, 0, 0.16)",
            },
            "& .MuiSlider-thumb:hover" : {
              boxShadow: "0px 0px 0px 8px rgba(0, 0, 0, 0.16)",
            },
            /* ⛔ 04/10/2026 — LE FOCUS CLAVIER SE VOIT. Avant choix, la poignee
               grise sur fond clair (contraste ~1,2:1) n'avait ni halo ni
               anneau : au clavier, ni le controle ni son focus ne se voyaient
               (relecture du 04/10). Anneau noir de 2 px, visible meme quand la
               poignee est masquee en Mobile. */
            "& .MuiSlider-thumb.Mui-focusVisible" : {
              boxShadow:"0px 0px 0px 2px #0A0A0B",
              opacity:1,
            },
            "& .MuiSlider-rail" : {
              /* Vertical, une fois choisi : le rail porte la couleur du
                 remplissage (voir `track`). */
              backgroundColor: (curseurVertical && !rienChoisi) ? (isAlreadyAnswer ? "#53D769" : "#0A0A0B") : GRIS_CURSEUR,
              opacity:1,
              /* Vertical : le trait de la planche va du haut du premier cran au
                 bas du dernier (3:282148, 220 px), il deborde donc du rail de
                 MUI de 6,5 px a chaque bout. */
              ...(curseurVertical ? { top:"-6.5px", height:"calc(100% + 13px)" } : {}),
            },
            "& .MuiSlider-mark" : {
              /* ⛔ CRANS VISIBLES (arbitrage Hicham, 21/09/2026). Ils etaient en
                 `display:none` depuis le commit 4963717 du 10/03/2026, sans une seule
                 ligne de justification : du code herite, anterieur au redesign 2026.
                 La planche 07 montre six points sur la piste. On suit la planche. */
              /* ⛔ 03/10/2026 — LE DESSIN DE LA PLANCHE : ronds de 13 px, du gris
                 du rail (3:261998, 3:282151). Le code posait des points noirs
                 de 6 px. Sous le rail rempli, ils s'effacent (planche
                 « choisi », 3:262025). */
              width:"13px", height:"13px", borderRadius:"100px",
              backgroundColor: GRIS_CURSEUR, opacity:1,
              ...(curseurVertical ? { left:"50%", transform:"translate(-50%, 50%)" } : { transform:"translate(-50%, -50%)" }),
            },
            /* Sous le rail rempli seulement. Avant tout choix, MUI tient quand
               meme pour « actifs » les crans de la poignee jusqu'au debut de la
               piste (en vertical `inverted`, de la poignee vers le haut) : on
               ne les efface qu'une fois une reponse choisie. */
            "& .MuiSlider-markActive" : rienChoisi ? {} : {
              backgroundColor:"transparent",
            },
            /* ⛔ 03/10/2026 — GRIS, SAUF LE LIBELLE DE LA POIGNEE. La planche
               pose les libelles a 70 % de noir (3:262005, 3:282140) et celui
               du palier choisi en noir (planche « choisi » 3:262025, et le
               premier tant que rien n'est choisi, 3:262004). Interlettrage 0 :
               celui de MUI (body2) ecartait les lettres. */
            /* `span.` : une cle distincte de celles des libelles extremes, plus bas. */
            /* En Mobile, avant tout choix, la planche n'a pas de poignee
               (3:282150) et tous ses libelles sont gris. */
            [`& span.MuiSlider-markLabel[data-index="${rienChoisi ? 0 : userChoice}"]`] : (rienChoisi && curseurVertical) ? {} : {
              color:"text.black",
            },
            "& .MuiSlider-markLabel" : {
              color:"rgba(0, 0, 0, 0.7)",
              letterSpacing:0,
              // 32px tronquait les libelles (« moitie du t... ») : on descend a
              // 18px et on laisse le texte passer sur deux lignes plutot que
              // de le couper.
              /* 16 / 20 aux trois paliers (3:262004, 3:282089, 3:282140). */
              fontSize:"16px",
              lineHeight:"20px",
              userSelect:"none",
              /* Haut des capitales a 20 px sous le centre du rail (3:262004,
                 y=671 ; 3:282089, y=873), soit 30 px sous la racine. */
              top:["33px", "30px", "30px", "30px"],
              // B14 : le libelle est en position absolue. Sans LARGEUR explicite
              // il se reduit a la place disponible a sa droite, nulle pour le
              // dernier cran (left:100%), donc il tombait au mot le plus long et
              // empilait le reste hors du cadre. Un maxWidth ne suffit pas :
              // il faut une largeur.
              /* ⚠️ EN POURCENTAGE DE LA PISTE, PLUS EN PIXELS. La largeur
                 etait calculee sur la constante de largeur de piste ; des que la piste
                 est plus etroite — c'est le cas dans la colonne de l'apercu —
                 les libelles restaient trop larges et se chevauchaient. Le
                 bloc contenant d'un libelle est la racine du curseur : un
                 pourcentage y designe donc bien une fraction de la piste, et
                 la regle du point B14 — une largeur explicite, pas un simple
                 maximum — reste respectee. */
              width:(100 / nbReponses) + "%",
              maxWidth:(100 / nbReponses) + "%",
              whiteSpace:"normal",
              textAlign:"center",
            },
            /* ⛔ LES DEUX LIBELLES EXTREMES SONT RAMENES DANS LE CADRE.
               MUI pose chaque libelle en absolu, centre sur son cran par un
               `translateX(-50%)`. Pour le premier cran, pose a 0 % de la piste,
               la moitie du libelle tombe donc HORS de la piste — et se fait
               rogner. Mesure avant correction : « pas du tout » s'affichait
               « u tout », et le dernier palier autant a droite. Ce sont les deux
               extremes de l'echelle, ceux qui lui donnent son sens.
               La planche fait la meme chose a sa maniere : ses libelles sont une
               rangee de six blocs egaux bornee a 892 px, donc aucun ne sort. On
               ancre donc le premier par sa gauche et le dernier par sa droite.
               ⚠️ Par `data-index`, pas par `:first-of-type` : les traits de cran
               et les libelles sont des freres, et l'ordre du DOM ne garantit pas
               lequel vient en premier. */
            ...(curseurVertical ? {
              /* Libelles a GAUCHE du rail, colonne de 300 px (node 3:282139). */
              /* ⛔ NE PAS TOUCHER A `top` NI A `transform` EN VERTICAL.
                 MUI place les libelles d'un curseur vertical par un `top` en
                 POURCENTAGE, cran par cran, et les recentre par un `transform`.
                 Les ecraser fait retomber les six libelles au meme endroit :
                 capture LAB a 393x855, « pas d'accord » et « D'accord » etaient
                 superposes. On ne change donc que l'axe horizontal — la colonne
                 de libelles a gauche du rail, comme le node 3:282139. */
              /* ⚠️ CETTE CLE REMPLACE ENTIEREMENT CELLE DU HAUT (meme nom dans le
                 meme objet) : corps, interligne, couleur et interlettrage sont
                 donc repetes ici. Sans eux, les libelles Mobile retombaient a
                 14 px avec l'interlettrage de MUI (comparateur, 03/10). */
              "& .MuiSlider-markLabel" : {
                fontSize:"16px",
                lineHeight:"20px",
                letterSpacing:0,
                color:"rgba(0, 0, 0, 0.7)",
                userSelect:"none",
                left:"auto",
                right:"32px",
                width:"300px",
                /* 300 px a 393 de large ; plus etroit, la colonne cede. */
                maxWidth:"calc(100vw - 93px)",
                /* Ferre a GAUCHE, comme la planche (3:282140, x=30). */
                textAlign:"left",
                whiteSpace:"normal",
              },
            } : {}),
            /* ⛔ 03/10/2026 — CENTRES AUX GRANDS PALIERS, COMME LA PLANCHE. Au
               palier Desktop, le libelle extreme centre sur son cran deborde la
               piste de 62 px, et la bande lui laisse 320 px de marge : rien ne
               rogne (a 1512 ; ~130 px a 1130). La planche les centre (3:262004
               a x=310 pour 140 de large, cran a 380). En Tablet la marge tombe
               sous la moitie du libelle :
               on garde l'ancrage qui empeche le rognage (B14). */
            /* ⚠️ A partir de trois reponses : a deux, le libelle extreme mesure
               la moitie de la piste et deborde d'autant ; au palier md (1130 px)
               il etait rogne de ~57 px (relecture du 04/10). */
            /* ⛔ 04/10/2026 — UN LIBELLE ANCRE NE DEPASSE PAS LA PLACE QUI LUI
               RESTE. Ancre par son bord, il occupe [cran, cran + largeur] alors
               que son voisin, centre, commence a un demi-libelle avant le cran
               suivant : en 16 px, « Palier 1 tres long libelle » tenait sur une
               ligne et chevauchait « Palier 2 » (photos du 04/10, Tablet).
               Largeur ancree = pas entre crans - demi-libelle =
               (n + 1) / (2 n (n - 1)) de la piste ; a deux reponses, les deux
               libelles sont ancres et se partagent la piste. */
            "& .MuiSlider-markLabel[data-index=\"0\"]" : curseurVertical ? {} : {
              transform: nbReponses >= 3 ? ["translateX(0%)", "translateX(0%)", "translateX(-50%)", "translateX(-50%)"] : "translateX(0%)",
              textAlign: nbReponses >= 3 ? ["left", "left", "center", "center"] : "left",
              width: largeurAncree, maxWidth: largeurAncree,
            },
            ["& .MuiSlider-markLabel[data-index=\"" + (nbReponses - 1) + "\"]"] : curseurVertical ? {} : {
              transform: nbReponses >= 3 ? ["translateX(-100%)", "translateX(-100%)", "translateX(-50%)", "translateX(-50%)"] : "translateX(-100%)",
              textAlign: nbReponses >= 3 ? ["right", "right", "center", "center"] : "right",
              width: largeurAncree, maxWidth: largeurAncree,
            }
          }}
          aria-labelledby="discrete-slider-restrict"
          step={null}
          valueLabelDisplay="off"
          marks={marks}
          max={format?.responses?.length}
          min={1}
        />
        </Box>
        </BandeReponse>
        </Box>
        </BlocIllustre>
      </CenterContainer>
      {/* ⛔ 01/10/2026 — `3-241993` ETAIT UNE FAUTE DE FRAPPE : le noeud est
          `3:261993` (la bande basse de la planche 07, 1512 x 400 a y=582). Un
          chiffre de travers, et la preuve ne pointait nulle part. Trouve par le
          cliquet `noeuds-figma-resolvables`, qui exige qu'un numero EXISTE.
          Pas de `BottomBar` : sur ce noeud le CTA est dans la rangee de
          controles de la bande, comme sur les autres ecrans de question. */}
    </Box>
  )
}


export default FormatQuestionnaireV2
